var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useState } from 'react';
import { Duration, Timestamp } from '@parca/client';
import { useGrpcMetadata, useParcaContext } from '@parca/components';
import { Query } from '@parca/parser';
import { getStepDuration } from '@parca/utilities';
import MetricsGraph from '../MetricsGraph';
import useDelayedLoader from '../useDelayedLoader';
export var useQueryRange = function (client, queryExpression, start, end) {
    var _a = useState({
        response: null,
        isLoading: true,
        error: null,
    }), state = _a[0], setState = _a[1];
    var metadata = useGrpcMetadata();
    useEffect(function () {
        void (function () { return __awaiter(void 0, void 0, void 0, function () {
            var stepDuration, call;
            return __generator(this, function (_a) {
                setState({
                    response: null,
                    isLoading: true,
                    error: null,
                });
                stepDuration = getStepDuration(start, end);
                call = client.queryRange({
                    query: queryExpression,
                    start: Timestamp.fromDate(new Date(start)),
                    end: Timestamp.fromDate(new Date(end)),
                    step: Duration.create(stepDuration),
                    limit: 0,
                }, { meta: metadata });
                call.response
                    .then(function (response) { return setState({ response: response, isLoading: false, error: null }); })
                    .catch(function (error) { return setState({ response: null, isLoading: false, error: error }); });
                return [2 /*return*/];
            });
        }); })();
    }, [client, queryExpression, start, end, metadata]);
    return state;
};
var ProfileMetricsGraph = function (_a) {
    var queryClient = _a.queryClient, queryExpression = _a.queryExpression, profile = _a.profile, from = _a.from, to = _a.to, setTimeRange = _a.setTimeRange, addLabelMatcher = _a.addLabelMatcher, onPointClick = _a.onPointClick;
    var _b = useQueryRange(queryClient, queryExpression, from, to), isLoading = _b.isLoading, response = _b.response, error = _b.error;
    var isLoaderVisible = useDelayedLoader(isLoading);
    var _c = useParcaContext(), loader = _c.loader, onError = _c.onError, perf = _c.perf;
    useEffect(function () {
        if (error !== null) {
            onError === null || onError === void 0 ? void 0 : onError(error, 'metricsGraph');
        }
    }, [error, onError]);
    useEffect(function () {
        if (response === null) {
            return;
        }
        perf === null || perf === void 0 ? void 0 : perf.markInteraction('Metrics graph render', response.series[0].samples.length);
    }, [perf, response]);
    if (isLoaderVisible) {
        return _jsx(_Fragment, { children: loader });
    }
    if (error !== null) {
        return (_jsxs("div", __assign({ className: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative", role: "alert" }, { children: [_jsx("strong", __assign({ className: "font-bold" }, { children: "Error! " })), _jsx("span", __assign({ className: "block sm:inline" }, { children: error.message }))] })));
    }
    var series = response === null || response === void 0 ? void 0 : response.series;
    if (series !== null && series !== undefined && (series === null || series === void 0 ? void 0 : series.length) > 0) {
        var handleSampleClick = function (timestamp, _value, labels) {
            onPointClick(timestamp, labels, queryExpression);
        };
        return (_jsx("div", __assign({ className: "dark:bg-gray-700 rounded border-gray-300 dark:border-gray-500", style: { borderWidth: 1 } }, { children: _jsx(MetricsGraph, { data: series, from: from, to: to, profile: profile, setTimeRange: setTimeRange, onSampleClick: handleSampleClick, onLabelClick: addLabelMatcher, width: 0, sampleUnit: Query.parse(queryExpression).profileType().sampleUnit }) })));
    }
    return (_jsx("div", __assign({ className: "grid grid-cols-1" }, { children: _jsx("div", __assign({ className: "py-20 flex justify-center" }, { children: _jsx("p", __assign({ className: "m-0" }, { children: "No data found. Try a different query." })) })) })));
};
export default ProfileMetricsGraph;
